import { AxiosError } from 'axios';

/**
 * Stable backend error codes that mean "an AI feature isn't available
 * right now" — NOT a real failure the clinician should see as a red error.
 * They map 1:1 to the reasons in /me/ai-capabilities so the FE can degrade
 * to the manual workflow with the right copy.
 *
 * Keep in sync with the codes thrown by MAIE.Backend:
 *  - ocr.service.ts            → AI_OCR_BACKEND_DISABLED, AI_OCR_FAILED, AI_OCR_DISABLED
 *  - heidi-llm.service.ts      → AI_SUMMARY_UNAVAILABLE
 *  - voice-to-soap.gateway     → TENANT_CAPPED_OUT, TENANT_QUOTA_EXCEEDED, ENTITLEMENT_REQUIRED
 *  - usage-meter.service.ts    → BOT_QUOTA_EXCEEDED
 *  - generic stack-down        → AI_UNAVAILABLE
 */
export const AI_UNAVAILABLE_CODES = new Set<string>([
  'AI_UNAVAILABLE',
  'AI_OCR_DISABLED',
  'AI_OCR_BACKEND_DISABLED',
  'AI_OCR_FAILED',
  'AI_SUMMARY_UNAVAILABLE',
  'TENANT_CAPPED_OUT',
  'TENANT_QUOTA_EXCEEDED',
  'BOT_QUOTA_EXCEEDED',
  'ENTITLEMENT_REQUIRED',
  // AiEntitlementGuard / assistant WS / scan — post-trial or out of token
  // budget. Degrade quietly + self-heal (refetch /me/ai-capabilities) so the
  // subscribe prompts appear instead of a red error toast.
  'AI_NOT_ENTITLED',
  'AI_OCR_NOT_ENTITLED',
  // AiEntitlementGuard / assistant WS / scribe gateway — the CLINIC has AI but
  // this member is on a records-only seat. Quiet degrade + self-heal, same as
  // above; note these deliberately do NOT match ENTITLEMENT_CODE_RE, because
  // the member can't fix it by subscribing.
  'AI_NO_SEAT',
  'SCRIBE_NO_AI_SEAT',
]);

/** Window event the apiClient dispatches when it sees an AI-unavailable error. */
export const AI_UNAVAILABLE_EVENT = 'maie:ai-unavailable';

interface ErrorEnvelope {
  code?: string;
  message?: string;
  error?: { code?: string };
}

/** Pull the stable `code` out of a backend error envelope, if present. */
export function aiErrorCode(error: unknown): string | null {
  const data = (error as AxiosError<ErrorEnvelope>)?.response?.data;
  return data?.code ?? data?.error?.code ?? null;
}

// Codes that mean "not entitled / out of quota" — a paywall the doctor can act
// on by subscribing — as opposed to a plain outage (disabled / stack down),
// which has no subscribe path.
const ENTITLEMENT_CODE_RE = /NOT_ENTITLED|QUOTA|CAPPED|ENTITLEMENT/i;

/**
 * True when the error is specifically an entitlement/quota paywall (the doctor
 * can resolve it by subscribing), vs a transient availability outage. Use this
 * to decide whether to show a "Subscribe" affordance.
 */
export function isEntitlementError(error: unknown): boolean {
  const code = aiErrorCode(error);
  return !!code && ENTITLEMENT_CODE_RE.test(code);
}

/**
 * True when an error represents an AI feature being unavailable (disabled,
 * out of quota, or stack down) rather than a genuine error. Callers use
 * this to swap a red error toast for a quiet, informational state.
 */
export function isAiUnavailableError(error: unknown): boolean {
  const code = aiErrorCode(error);
  if (code && AI_UNAVAILABLE_CODES.has(code)) return true;
  // Fall back to status: a 503 from any AI surface is an availability
  // signal, not a clinician-facing bug.
  const status = (error as AxiosError)?.response?.status;
  return status === 503;
}
